/**
 * Converts legacy markdown document content to HTML.
 * Used only when loading docs that were saved as markdown/sections.
 * The editor works only with HTML; this is for one-time migration on load.
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown || !markdown.trim()) return '';

  const lines = markdown.split('\n');
  let html = '';
  let inTable = false;

  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const processInline = (text: string): string => {
    let s = escapeHtml(text);
    s = s.replace(/`([^`]+?)`/g, '<code class="bg-[color:var(--chat-code-bg)] text-[color:var(--accent-pink-500)] px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
    s = s.replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-bold">$1</strong>');
    s = s.replace(/\*([^*]+?)\*/g, '<em class="italic">$1</em>');
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[color:var(--nt-primary)] underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>');
    return s;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith('# ')) {
      if (inTable) { html += '</tbody></table>'; inTable = false; }
      html += `<h1 class="text-3xl font-bold mb-4 mt-6 theme-text leading-tight">${escapeHtml(line.slice(2))}</h1>`;
      continue;
    }
    if (line.startsWith('## ')) {
      if (inTable) { html += '</tbody></table>'; inTable = false; }
      html += `<h2 class="text-2xl font-bold mb-3 mt-5 theme-text leading-tight">${escapeHtml(line.slice(3))}</h2>`;
      continue;
    }
    if (line.startsWith('### ')) {
      if (inTable) { html += '</tbody></table>'; inTable = false; }
      html += `<h3 class="text-xl font-bold mb-2 mt-4 theme-text leading-tight">${escapeHtml(line.slice(4))}</h3>`;
      continue;
    }
    if (line.startsWith('#### ')) {
      if (inTable) { html += '</tbody></table>'; inTable = false; }
      html += `<h4 class="text-lg font-bold mb-2 mt-3 theme-text leading-tight">${escapeHtml(line.slice(5))}</h4>`;
      continue;
    }

    if (line.startsWith('|') && line.endsWith('|')) {
      if (line.match(/^\|[\s\-:]+\|$/)) continue;
      if (!inTable) {
        html += '<table class="w-full my-4 border-collapse border theme-border"><thead><tr class="bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)]">';
        inTable = true;
      }
      const cells = line.split('|').slice(1, -1).map(c => c.trim());
      const isHeader = i < lines.length - 1 && lines[i + 1].trim().match(/^\|[\s\-:]+\|$/);
      if (isHeader) {
        html += cells.map(c => `<th class="px-4 py-2 border theme-border theme-text font-semibold">${processInline(c)}</th>`).join('');
        html += '</tr></thead><tbody>';
      } else {
        html += '<tr>' + cells.map(c => `<td class="px-4 py-2 border theme-border theme-text">${processInline(c)}</td>`).join('') + '</tr>';
      }
      continue;
    }

    if (inTable && line !== '') {
      html += '</tbody></table>';
      inTable = false;
    }

    if (line === '') {
      html += '<p class="m-0 theme-text leading-normal"><br /></p>';
      continue;
    }

    html += `<p class="m-0 theme-text leading-normal">${processInline(line)}</p>`;
  }

  if (inTable) html += '</tbody></table>';
  return html;
}

/** Returns true if the string looks like HTML (e.g. starts with < and contains tags). */
export function looksLikeHtml(s: string): boolean {
  const t = s.trim();
  return t.length > 0 && t.startsWith('<') && /<[a-z][\s\S]*>/i.test(t);
}
